import { Badge, Skeleton, Switch, Table, Text, Tooltip } from '@radix-ui/themes';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router';
import { Building2, MapPin, Hash } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '../../constants/featureFlags';
import ConfirmationModal from '../elements/ConfirmationModal';
import ExternalJobRef from '../elements/ExternalJobRef';

const getLocationText = (location: { name: string; city: string; country: string }) => {
  return location?.name || location?.city || location?.country || null;
};

const getLocationBadge = (location: { name: string; city: string; country: string }) => {
  const badgeText = location?.name || location?.city || location?.country;

  return badgeText ? (
    <Badge className="ml-1" radius="large">
      {badgeText}
    </Badge>
  ) : null;
};

const ListJobs = ({
  jobs,
  onJobActiveToggle,
  size,
  loading = false,
}: {
  jobs: {
    _id: string;
    id: string;
    title: string;
    createdAt: string;
    active: boolean;
    company: string;
    location: {
      name: string;
      city: string;
      country: string;
    };
    externalRef: {
      externalId: string;
      source: string;
    };
    total: { totalCount: number };
    new: { totalCount: number };
    leads: { totalCount: number };
    accepted: { totalCount: number };
    hired: { totalCount: number };
    notHired: { totalCount: number };
  }[];
  onJobActiveToggle?: (job: { id: string; active: boolean; title: string }) => void;
  size?: string;
  loading?: boolean;
}) => {
  const { t } = useTranslation();
  const [showConfirmationToggleModal, setShowConfirmationToggleModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<{ id: string; active: boolean; title: string }>();
  const isEnhancedJobListLayoutEnabled = useFeatureFlagEnabled(FeatureFlags.ENABLE_ENHANCED_JOB_LIST_LAYOUT) ?? false;
  const hasExternalJobReferences = useMemo(() => jobs?.some((job) => !!job.externalRef?.externalId) ?? false, [jobs]);

  const toggleJobActiveStatus = (job: { id: string; active: boolean; title: string }) => {
    if (job.active === true) {
      setShowConfirmationToggleModal(true);
    } else if (onJobActiveToggle) {
      onJobActiveToggle(job);
    }
    setSelectedJob(job);
  };

  const handleJobToggleConfirm = () => {
    if (selectedJob && onJobActiveToggle) {
      onJobActiveToggle(selectedJob);
    }
    setSelectedJob(undefined);
    setShowConfirmationToggleModal(false);
  };

  const handleJobToggleClose = () => {
    setShowConfirmationToggleModal(false);
  };
  let small = true;
  if (size === 'small') small = false;

  return (
    <>
      <ConfirmationModal
        message={[<p key="a">{t('listJobs.deactivate.confirmMessage', { jobTitle: selectedJob?.title ?? '' })}</p>]}
        show={showConfirmationToggleModal}
        onHide={handleJobToggleClose}
        onConfirm={handleJobToggleConfirm}
        confirmButtonText={t('listJobs.deactivate.confirmButton')}
      />
      <Table.Root className="mb-4" size="1">
        <Table.Header>
          <Table.Row align="center">
            <Table.ColumnHeaderCell>{t('listJobs.columns.job')}</Table.ColumnHeaderCell>
            {hasExternalJobReferences && (
              <Tooltip side="left" content={<Text size="3">Job reference ID in your integration account.</Text>}>
                <Table.ColumnHeaderCell width="5rem" justify="start">
                  Job Reference ID
                </Table.ColumnHeaderCell>
              </Tooltip>
            )}
            <Tooltip side="left" content={<Text size="3">{t('listJobs.tooltips.totalApplicants')}</Text>}>
              <Table.ColumnHeaderCell width="5rem" justify="center">
                {t('listJobs.columns.totalApplicants')}
              </Table.ColumnHeaderCell>
            </Tooltip>
            {small && (
              <Tooltip side="left" content={<Text size="3">{t('listJobs.tooltips.invitedApplicants')}</Text>}>
                <Table.ColumnHeaderCell width="5rem" justify="center">
                  {t('listJobs.columns.invitedApplicants')}
                </Table.ColumnHeaderCell>
              </Tooltip>
            )}
            <Tooltip side="left" content={<Text size="3">{t('listJobs.tooltips.hubertLeads')}</Text>}>
              <Table.ColumnHeaderCell width="5rem" justify="center">
                {t('listJobs.columns.hubertLeads')}
              </Table.ColumnHeaderCell>
            </Tooltip>
            <Tooltip side="left" content={<Text size="3">{t('listJobs.tooltips.acceptedLeads')}</Text>}>
              <Table.ColumnHeaderCell width="5rem" justify="center">
                {t('listJobs.columns.acceptedLeads')}
              </Table.ColumnHeaderCell>
            </Tooltip>
            {small && (
              <Tooltip side="left" content={<Text size="3">{t('listJobs.tooltips.hired')}</Text>}>
                <Table.ColumnHeaderCell width="5rem" justify="center">
                  {t('listJobs.columns.hired')}
                </Table.ColumnHeaderCell>
              </Tooltip>
            )}

            <Tooltip side="left" content={<Text size="3">{t('listJobs.tooltips.notHired')}</Text>}>
              <Table.ColumnHeaderCell width="5rem" justify="center">
                {t('listJobs.columns.notHired')}
              </Table.ColumnHeaderCell>
            </Tooltip>

            {small && <Table.ColumnHeaderCell width="3rem" />}
            {small && (
              <Table.ColumnHeaderCell width="6rem" justify="center">
                {t('listJobs.columns.created')}
              </Table.ColumnHeaderCell>
            )}
            {small && (
              <Tooltip side="left" content={<Text size="3">{t('listJobs.tooltips.active')}</Text>}>
                <Table.ColumnHeaderCell width="3rem">{t('listJobs.columns.active')}</Table.ColumnHeaderCell>
              </Tooltip>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {loading &&
            Array.from({ length: 6 }, (_, index) => {
              const titleWidths = [95, 87, 100, 76, 95, 100];

              return (
                <Table.Row key={`skeleton-${index}`}>
                  <Table.Cell style={{ verticalAlign: 'middle' }}>
                    <Skeleton width={`${titleWidths[index]}%`} />
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <Skeleton width="24px" />
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <Skeleton width="24px" />
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <Skeleton width="24px" />
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <Skeleton width="24px" />
                  </Table.Cell>
                  {small && (
                    <>
                      <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Skeleton width="24px" />
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Skeleton width="24px" />
                      </Table.Cell>
                      <Table.Cell style={{ verticalAlign: 'middle' }}></Table.Cell>
                      <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Skeleton />
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Skeleton width="20px" height="20px" style={{ borderRadius: '50%' }} />
                      </Table.Cell>
                    </>
                  )}
                </Table.Row>
              );
            })}

          {jobs && !small && jobs.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={5} className="text-left tx-1" style={{ verticalAlign: 'middle' }}>
                {t('listJobs.empty')}
              </Table.Cell>
            </Table.Row>
          )}

          {jobs && small && jobs.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={10} className="text-left tx-1" style={{ verticalAlign: 'middle' }}>
                <span className="text-left tx-1">{t('listJobs.empty')}</span>
              </Table.Cell>
            </Table.Row>
          )}

          {jobs &&
            jobs.map((job, index) => {
              return (
                <Table.Row key={`job-${index}`} className="hoverable">
                  {/* maxWidth doesn't actually set it to 300px and acts more like auto/fill */}
                  <Table.Cell className="truncate" maxWidth="300px" style={{ verticalAlign: 'middle' }}>
                    <NavLink
                      key={`job-link-${index}-${job._id}`}
                      end
                      to={'/job/' + job.id}
                      style={{
                        color: 'var(--black)',
                        textDecoration: 'none',
                      }}
                    >
                      {isEnhancedJobListLayoutEnabled ? (
                        <>
                          <div>
                            <Tooltip content={<Text size="3">{job.title}</Text>}>
                              <span>{job.title}</span>
                            </Tooltip>
                          </div>
                          {(job.company || getLocationText(job.location) || job.externalRef?.externalId) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                              {job.company && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                  <Building2 size={14} style={{ color: 'var(--gray-9)' }} />
                                  <Text size="2" color="gray">
                                    {job.company}
                                  </Text>
                                </div>
                              )}
                              {getLocationText(job.location) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                  <MapPin size={14} style={{ color: 'var(--gray-9)' }} />
                                  <Text size="2" color="gray">
                                    {getLocationText(job.location)}
                                  </Text>
                                </div>
                              )}
                              {job.externalRef?.externalId && (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <Hash size={14} style={{ color: 'var(--gray-9)' }} />
                                  <Text size="2" color="gray">
                                    {job.externalRef.externalId}
                                  </Text>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div>
                          <Tooltip content={<Text size="3">{job.title}</Text>}>
                            <span>{job.title}</span>
                          </Tooltip>
                          {getLocationBadge(job.location)}
                        </div>
                      )}
                    </NavLink>
                  </Table.Cell>
                  {hasExternalJobReferences && (
                    <Table.Cell justify="start" style={{ verticalAlign: 'middle' }}>
                      <ExternalJobRef externalId={job.externalRef?.externalId} source={job.externalRef?.source} />
                    </Table.Cell>
                  )}
                  <Table.Cell justify="center" style={{ verticalAlign: 'middle' }}>
                    <strong>{job ? job.total.totalCount : 0}</strong>
                  </Table.Cell>
                  {small && (
                    <Table.Cell justify="center" className="color dark-grey" style={{ verticalAlign: 'middle' }}>
                      <strong>{job ? job.new.totalCount : 0}</strong>
                    </Table.Cell>
                  )}
                  <Table.Cell justify="center" className="color purple" style={{ verticalAlign: 'middle' }}>
                    <strong>{job ? job.leads.totalCount : 0}</strong>
                  </Table.Cell>
                  <Table.Cell justify="center" className="color green" style={{ verticalAlign: 'middle' }}>
                    <strong>{job ? job.accepted.totalCount : 0}</strong>
                  </Table.Cell>
                  {small && (
                    <Table.Cell justify="center" className="color purple" style={{ verticalAlign: 'middle' }}>
                      <strong>{job ? job.hired.totalCount : 0}</strong>
                    </Table.Cell>
                  )}
                  <Table.Cell justify="center" className="color red" style={{ verticalAlign: 'middle' }}>
                    <strong>{job ? job.notHired.totalCount : 0}</strong>
                  </Table.Cell>
                  {small && (
                    <>
                      <Table.Cell style={{ verticalAlign: 'middle' }}></Table.Cell>
                      <Table.Cell justify="center" style={{ verticalAlign: 'middle' }}>
                        <Tooltip content={<Text size="3">{new Date(job.createdAt).toLocaleString('sv-se')}</Text>}>
                          <span>{new Date(job.createdAt).toLocaleDateString('sv-se')}</span>
                        </Tooltip>
                      </Table.Cell>

                      <Table.Cell justify="center" style={{ verticalAlign: 'middle' }}>
                        <Tooltip
                          content={
                            <Text size="3">
                              {job.active ? t('listJobs.activeTooltip.active') : t('listJobs.activeTooltip.inactive')}
                            </Text>
                          }
                        >
                          <Switch
                            id={`custom-switch-${index}`}
                            color={job.active ? 'grass' : 'red'}
                            checked={job.active}
                            onClick={() => {
                              toggleJobActiveStatus(job);
                            }}
                          />
                        </Tooltip>
                      </Table.Cell>
                    </>
                  )}
                </Table.Row>
              );
            })}
        </Table.Body>
        {!small && jobs && jobs.length !== 0 && (
          <tfoot className="text-center">
            <Table.Row>
              <Table.Cell colSpan={5} style={{ verticalAlign: 'middle' }}>
                <Link
                  to="/jobs"
                  style={{
                    color: 'var(--black)',
                    textDecoration: 'none',
                  }}
                >
                  {t('listJobs.viewAllLink')}
                </Link>
              </Table.Cell>
            </Table.Row>
          </tfoot>
        )}
      </Table.Root>
    </>
  );
};

export default ListJobs;
